import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

/**
 * Remake-hold dialogs (jobq-api "Remake hold", 2026-09-25/26). Imperative, like utils/pinPrompt:
 * each call mounts a Bootstrap modal and resolves with the operator's answer.
 *
 *   remakeHoldChoice(hold)          -> "wait" | "ack" | "close_short" | "release"
 *       hold = the 409 detail {level, message, can_close_short, close_short_assemblies,
 *       close_short_note}. "confirm" offers "they're here" (remake_ack); "block" offers close
 *       short (only when the API says it would succeed) and a manager release.
 *   remakeHeadsUp(preview, {lead})  -> undefined, once acknowledged
 *       preview = POST /defects' remake_hold_preview: what the new loss holds back at the join.
 *   remakeAttachPicker(demand, candidates) -> {makeup_job_id | makeup_job_num, qty, label} | null
 *       GET /remake_demands/{id}/link_candidates -> pick the replacement job (or type a job #).
 */
function mount(render) {
  return new Promise((resolve) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);
    const done = (value) => {
      resolve(value);
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 0);
    };
    root.render(render(done));
  });
}

export function remakeHoldChoice(hold) {
  return mount((done) => <HoldChoiceModal hold={hold || {}} onDone={done} />);
}

function HoldChoiceModal({ hold, onDone }) {
  const [show, setShow] = useState(true);
  const block = hold.level !== "confirm";
  const close = (value) => {
    setShow(false);
    onDone(value);
  };
  return (
    <Modal show={show} onHide={() => close("wait")} centered style={{ zIndex: 2000 }}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>
          {block ? "⛔ This set is waiting on replacement parts" : "⏳ Replacement parts on the way"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ whiteSpace: "pre-wrap", marginBottom: "0.5rem" }}>{hold.message}</p>
        {block && !hold.can_close_short && hold.close_short_note && (
          <p className="small text-muted mb-0">Closing it short isn't available: {hold.close_short_note}</p>
        )}
      </Modal.Body>
      <Modal.Footer style={{ flexDirection: "column", alignItems: "stretch", gap: "0.4rem" }}>
        {!block && (
          <Button variant="success" onClick={() => close("ack")}>
            They&apos;re here and welded in: complete it
          </Button>
        )}
        {block && hold.can_close_short && (
          <Button variant="warning" onClick={() => close("close_short")}>
            Close this set short: the customer takes {hold.close_short_assemblies} fewer (manager)
          </Button>
        )}
        <Button variant="outline-danger" onClick={() => close("release")}>
          Manager release: the replacements were made another way
        </Button>
        <Button variant="secondary" onClick={() => close("wait")}>
          Wait for the replacements (don&apos;t complete)
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function remakeHeadsUp(preview, { lead } = {}) {
  return mount((done) => <HeadsUpModal preview={preview || {}} lead={lead} onDone={() => done(undefined)} />);
}

function HeadsUpModal({ preview, lead, onDone }) {
  const [show, setShow] = useState(true);
  const close = () => {
    setShow(false);
    onDone();
  };
  return (
    <Modal show={show} onHide={close} centered style={{ zIndex: 2000 }}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>⚠ Heads up: replacement parts needed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {lead && <p className="fw-bold mb-2">{lead}</p>}
        <p style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{preview.message}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={close}>Got it</Button>
      </Modal.Footer>
    </Modal>
  );
}

export function remakeAttachPicker(demand, candidates) {
  return mount((done) => (
    <AttachModal demand={demand || {}} candidates={candidates || []} onDone={done} />
  ));
}

function AttachModal({ demand, candidates, onDone }) {
  const [show, setShow] = useState(true);
  const first = candidates[0];
  const [choice, setChoice] = useState(first ? String(first.job_id) : "other");
  const [otherNum, setOtherNum] = useState("");
  const [qty, setQty] = useState(String((first && first.suggested_qty) || demand.gap || 1));
  const [error, setError] = useState("");
  const close = (value) => {
    setShow(false);
    onDone(value);
  };
  const pick = (value) => {
    setChoice(value);
    const c = candidates.find((x) => String(x.job_id) === value);
    if (c) setQty(String(c.suggested_qty || 1));
    setError("");
  };
  const submit = () => {
    const n = parseInt(qty, 10);
    if (!n || n < 1 || n > (demand.gap || n)) {
      setError(`Enter 1 to ${demand.gap} pieces.`);
      return;
    }
    if (choice === "other") {
      const num = parseInt(otherNum, 10);
      if (!num) {
        setError("Enter the replacement job's number.");
        return;
      }
      close({ makeup_job_num: num, qty: n, label: `#${num}` });
      return;
    }
    const c = candidates.find((x) => String(x.job_id) === choice);
    close({ makeup_job_id: c.job_id, qty: n, label: `#${c.job_num}` });
  };
  return (
    <Modal show={show} onHide={() => close(null)} centered style={{ zIndex: 2000 }}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Attach a replacement job</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          Job {demand.source_job_num} still needs <strong>{demand.gap}</strong> replacement{" "}
          <strong>{demand.product_id}</strong>
          {demand.sales_order_number ? ` (${demand.sales_order_number})` : " (stock job)"}. Which job made them?
        </p>
        {candidates.length === 0 && (
          <p className="small text-muted">
            No replacement job of this customer (or order) has {demand.product_id} left to link. Enter the job
            number if you know it.
          </p>
        )}
        <p className="small text-muted mb-2">
          Only a job cut as replacements counts: not one that welds or ships {demand.product_id} itself, and not
          another revision.
        </p>
        {candidates.map((c) => (
          <div className="form-check" key={c.job_id}>
            <input
              className="form-check-input"
              type="radio"
              name="remake-replacement"
              id={`remake-cand-${c.job_id}`}
              checked={choice === String(c.job_id)}
              onChange={() => pick(String(c.job_id))}
            />
            <label className="form-check-label" htmlFor={`remake-cand-${c.job_id}`}>
              Job #{c.job_num}
              {c.sales_order_number ? ` · ${c.sales_order_number}` : " · stock"} · {c.available} not yet linked
              {!c.where ? " · ready" : c.where === "finished short" || c.where === "not started"
                ? ` · ${c.where}` : ` · at ${c.where}`}
              {c.same_order && <span className="badge bg-info ms-1">same order</span>}
            </label>
          </div>
        ))}
        <div className="form-check d-flex align-items-center gap-2 mt-1">
          <input
            className="form-check-input"
            type="radio"
            name="remake-replacement"
            id="remake-cand-other"
            checked={choice === "other"}
            onChange={() => pick("other")}
          />
          <label className="form-check-label" htmlFor="remake-cand-other">Another job #</label>
          <input
            type="number"
            className="form-control form-control-sm"
            style={{ maxWidth: "8rem" }}
            aria-label="Replacement job number"
            value={otherNum}
            onChange={(e) => {
              setOtherNum(e.target.value);
              setChoice("other");
            }}
          />
        </div>
        <div className="d-flex align-items-center gap-2 mt-3">
          <label htmlFor="remake-attach-qty" className="mb-0">Pieces</label>
          <input
            id="remake-attach-qty"
            type="number"
            min="1"
            max={demand.gap}
            className="form-control form-control-sm"
            style={{ maxWidth: "6rem" }}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </div>
        {error && <div className="text-danger small mt-2">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => close(null)}>Cancel</Button>
        <Button variant="primary" onClick={submit}>Attach (manager PIN next)</Button>
      </Modal.Footer>
    </Modal>
  );
}
